import { WorkspacePremiumOutlined } from "@mui/icons-material";
import { Avatar, Box, CircularProgress, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import logo from "../../assets/logo.png";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IExhibition } from "../../interfaces/IExhibition";
import { IJudgment } from "../../interfaces/IJudgment";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { downloadBase64Pdf, downloadBase64Zip } from "../../utils/downloadPdf";
import { Medal, PlantBox, ResultsPaper } from "./Results.styles";

/**
 * Este arquivo é baseado na tela de "Follow Up", mas será focado nos resultados finais da exposição.
 * Regras e lógica para cálculo dos resultados:
 * (ver comentário original)
 */

export const Results: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;
  const { user } = useAuth();
  const toastr = useToastr();
  const [exhibition, setExhibition] = useState<IExhibition | null>(null);
  const [loading, setLoading] = useState(false);
  const [certLoading, setCertLoading] = useState(false);
  const [loadingCertJudgments, setLoadingCertJudgments] = useState<Set<string>>(new Set());

  const exhibitionId = window.location.pathname.replace(
    /^\/(follow-up|results)\//,
    "",
  );

  const handleDownloadCertificate = useCallback(
    async (judgmentId: string, plantId: string) => {
      if (!judgmentId || !plantId) return;
      setCertLoading(true);
      try {
        const response = await api.get(
          `/judgments/${judgmentId}/certificates/${plantId}`,
        );
        const { certificates } = response.data as {
          certificates: { filename: string; base64: string }[];
        };
        if (certificates?.length) {
          downloadBase64Pdf(certificates[0].base64, certificates[0].filename);
        }
      } catch (error: any) {
        toastr.error(
          error?.response?.data?.message || "Erro ao gerar certificado",
        );
      } finally {
        setCertLoading(false);
      }
    },
    [toastr],
  );

  const handleGenerateCertsByCategory = useCallback(
    async (judgmentId: string) => {
      if (!judgmentId) return;
      setLoadingCertJudgments(prev => new Set(prev).add(judgmentId));
      try {
        const response = await api.get(`/judgments/${judgmentId}/certificates`);
        const { zip } = response.data as { zip: { filename: string; base64: string } };
        if (!zip) {
          toastr.error("Nenhum certificado de pódio encontrado para esta categoria.");
          return;
        }
        downloadBase64Zip(zip.base64, zip.filename);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 422) {
          toastr.error("Nenhuma planta no pódio. Execute a consolidação antes de gerar certificados.");
        } else {
          toastr.error(error?.response?.data?.message || "Erro ao gerar certificados");
        }
      } finally {
        setLoadingCertJudgments(prev => {
          const next = new Set(prev);
          next.delete(judgmentId);
          return next;
        });
      }
    },
    [toastr],
  );

  const handleListExhibition = useCallback(async () => {
    if (!exhibitionId) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/exhibitions/complete/${exhibitionId}?allJudges=true`,
      );
      setExhibition(response.data);
    } catch (error: any) {
      setExhibition(null);
    } finally {
      setLoading(false);
    }
  }, [exhibitionId]);

  useEffect(() => {
    handleListExhibition();
  }, [handleListExhibition]);

  // Utilitários de ordenação
  const sortCategories = (categories: any[] = []) =>
    categories.slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

  // Agrupa julgamentos por categoria
  const getJudgmentsByCategory = () => {
    if (!exhibition?.judgments) return [];
    const grouped: Record<string, any> = {};
    exhibition.judgments.forEach((judgment: IJudgment) => {
      const catId = judgment.category?.id || "sem-categoria";
      if (!grouped[catId]) {
        grouped[catId] = {
          category: judgment.category,
          plants: [],
          judgmentCriteria: judgment.judgmentCriteria,
          sequence: judgment.category?.sequence ?? 0,
          parentCategoryId: judgment?.parentCategoryId || null,
          parentCategory: judgment?.parentCategory || null,
          judgmentId: judgment.id,
        };
      }
      grouped[catId].plants = grouped[catId].plants.concat(
        (judgment.plants || []).map(p => ({ ...p, _judgmentId: judgment.id })),
      );
      grouped[catId].judgmentCriteria = judgment.judgmentCriteria;
    });
    return sortCategories(Object.values(grouped));
  };

  // Interfaces usadas em mais de uma função
  interface Evaluation {
    userId: string;
    score?: number;
    coordinatorScore?: number;
    [key: string]: any;
  }

  interface PlantWithScores extends Record<string, any> {
    evaluations?: Evaluation[];
    finalScore: number;
    judgeTotals: number[];
  }

  // Calcula as notas finais das plantas conforme as regras
  const calculateFinalScores = (categories: any[]) => {
    return categories.map(category => {
      const plantsWithScores: PlantWithScores[] = category.plants.map(
        (plant: Record<string, any>) => {
          // Agrupa avaliações por juiz
          const evaluationsByJudge: Record<string, Evaluation[]> = {};
          (plant.evaluations || []).forEach((ev: Evaluation) => {
            if (!evaluationsByJudge[ev.userId]) {
              evaluationsByJudge[ev.userId] = [];
            }
            evaluationsByJudge[ev.userId].push(ev);
          });

          // Soma dos critérios por juiz, incluindo coordinatorScore
          const judgeTotals: number[] = Object.values(evaluationsByJudge).map(
            evals =>
              evals.reduce(
                (sum, ev) => sum + (ev.score ?? 0) + (ev.coordinatorScore ?? 0),
                0,
              ),
          );

          // Calcula nota final conforme regra dos juízes
          let finalScore = 0;
          if (judgeTotals.length > 3) {
            const sorted = [...judgeTotals].sort((a, b) => a - b);
            const trimmed = sorted.slice(1, -1); // remove menor e maior
            finalScore =
              trimmed.reduce((sum, v) => sum + v, 0) / trimmed.length || 0;
          } else if (judgeTotals.length > 0) {
            // Para 3 ou menos, soma todas e divide pela quantidade
            finalScore =
              judgeTotals.reduce((sum, v) => sum + v, 0) / judgeTotals.length;
          }

          return {
            ...plant,
            finalScore,
            judgeTotals,
          };
        },
      );

      // Ordena plantas pela nota final (maior para menor)
      plantsWithScores.sort((a, b) => b.finalScore - a.finalScore);

      return {
        ...category,
        plants: plantsWithScores,
      };
    });
  };

  // Remova o uso de processWinners e use apenas calculateFinalScores
  const categories = calculateFinalScores(getJudgmentsByCategory());

  // Função para formatar data
  function formatDate(dateStr?: string) {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR");
  }

  // Função para agrupar categorias por parentCategoryId e pegar a melhor planta de cada grupo
  const getBestByParentCategory = (categories: any[]) => {
    // Agrupa por parentCategoryId, se não tiver pai, usa o próprio id como "pai"
    const grouped: Record<string, { parent: any; plants: PlantWithScores[] }> =
      {};
    categories.forEach(cat => {
      // Se não tiver parentCategoryId, a categoria é "pai" dela mesma
      const parentId = cat?.parentCategoryId || cat?.category?.id || "sem-pai";
      if (!grouped[parentId]) {
        grouped[parentId] = {
          parent:
            cat?.parentCategory ||
            (!cat?.parentCategoryId ? cat.category : null),
          plants: [],
        };
      }
      grouped[parentId].plants.push(...cat.plants);
    });

    // Para cada grupo, pega a planta de maior nota
    return Object.values(grouped)
      .filter(group => group.plants.length > 0)
      .map(group => {
        const bestPlant = group.plants.reduce((max, plant) =>
          plant.finalScore > max.finalScore ? plant : max,
        );
        return {
          parentName: group.parent?.name || "Sem categoria pai",
          plant: bestPlant,
        };
      });
  };

  const bestByParent = getBestByParentCategory(categories);

  return (
    <>
      {(loading || certLoading) && <BackdropCustom />}
      <Box sx={{ p: { xs: 1, md: 2 }, width: "90vw", mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            mb: 2,
            gap: 1,
          }}
        >
          <Avatar
            src={logo}
            alt="Logo"
            sx={{ width: 70, height: 70, mb: 1, bgcolor: "transparent" }}
            variant="square"
          />
          <Typography variant="h5" fontWeight={700} align="center">
            Relatório de Resultados
          </Typography>
          <Typography variant="h6" fontWeight={500} align="center">
            {exhibition?.name}
          </Typography>
          <Typography variant="subtitle1" fontWeight={400} align="center">
            Data: {formatDate(exhibition?.date ?? undefined)}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          fontWeight={600}
          align="left"
          sx={{ mb: 2, color: "text.primary" }}
        >
          Julgamento por categorias
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {exhibition &&
          categories.map((cat, idx) => {
            // Verifica se a melhor planta da categoria pai também está nesta categoria filha
            const bestParent = bestByParent.find(
              b =>
                b.plant.id &&
                cat.plants.some((p: PlantWithScores) => p.id === b.plant.id),
            );
            // Se sim, coloca ela em primeiro
            let plantsToShow = cat.plants;
            if (bestParent) {
              const bestIdx = cat.plants.findIndex(
                (p: PlantWithScores) => p.id === bestParent.plant.id,
              );
              if (bestIdx > 0) {
                plantsToShow = [
                  cat.plants[bestIdx],
                  ...cat.plants.slice(0, bestIdx),
                  ...cat.plants.slice(bestIdx + 1),
                ];
              }
            }

            return (
              <ResultsPaper
                key={cat.category?.id || idx}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  mb: 2,
                  background: "#fafbfc",
                  boxShadow: "none",
                  borderRadius: 1,
                }}
                elevation={0}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    gutterBottom
                    color="primary"
                    sx={{ mb: 0 }}
                  >
                    {cat.category?.sequence} -{" "}
                    {cat.category?.name || "Sem categoria"}
                  </Typography>
                  {user?.typeId === 1 && exhibition?.finished && cat.judgmentId && (
                    <Tooltip title="Gerar certificados desta categoria" placement="top" arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={loadingCertJudgments.has(cat.judgmentId)}
                          onClick={() => handleGenerateCertsByCategory(cat.judgmentId)}
                        >
                          {loadingCertJudgments.has(cat.judgmentId)
                            ? <CircularProgress size={18} color="inherit" />
                            : <WorkspacePremiumOutlined fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Divider sx={{ mb: 1 }} />
                {plantsToShow.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontStyle: "italic" }}
                  >
                    Não houveram plantas nesta categoria.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    {plantsToShow.map((plant: PlantWithScores, pos: number) => {
                      // Se for a melhor da categoria pai, aplica apenas o destaque visual, sem texto "Melhor ..."
                      const isBestParent =
                        bestParent &&
                        plant.id === bestParent.plant.id &&
                        pos === 0;

                      // Troféu para campeã da categoria pai
                      const trophy = isBestParent ? (
                        <span style={{ marginRight: 4, fontSize: "1.2em" }}>
                          🏆
                        </span>
                      ) : null;

                      // Ajuste: se a melhor planta da categoria pai está nesta categoria,
                      // o segundo lugar recebe ouro, terceiro prata, quarto bronze
                      let medal = null;
                      if (bestParent && plantsToShow.length > 1) {
                        if (pos === 1) {
                          medal = (
                            <Medal color="#FFD700" style={{ marginRight: 4 }}>
                              🥇
                            </Medal>
                          );
                        } else if (pos === 2) {
                          medal = (
                            <Medal color="#C0C0C0" style={{ marginRight: 4 }}>
                              🥈
                            </Medal>
                          );
                        } else if (pos === 3) {
                          medal = (
                            <Medal color="#CD7F32" style={{ marginRight: 4 }}>
                              🥉
                            </Medal>
                          );
                        }
                      } else {
                        // Caso normal: 1º ouro, 2º prata, 3º bronze
                        if (pos === 0) {
                          medal = (
                            <Medal color="#FFD700" style={{ marginRight: 4 }}>
                              🥇
                            </Medal>
                          );
                        } else if (pos === 1) {
                          medal = (
                            <Medal color="#C0C0C0" style={{ marginRight: 4 }}>
                              🥈
                            </Medal>
                          );
                        } else if (pos === 2) {
                          medal = (
                            <Medal color="#CD7F32" style={{ marginRight: 4 }}>
                              🥉
                            </Medal>
                          );
                        }
                      }

                      const isPodiumPlant = medal !== null || trophy !== null;
                      const canDownloadCert =
                        isPodiumPlant && user?.typeId === 1 && !!plant._judgmentId;

                      return (
                        <Tooltip
                          key={`${cat.category?.id}-${plant.id}`}
                          title={
                            canDownloadCert
                              ? "Clique para baixar o certificado desta planta"
                              : ""
                          }
                          placement="top"
                          arrow
                        >
                          <PlantBox
                            onClick={
                              canDownloadCert
                                ? () => handleDownloadCertificate(plant._judgmentId, plant.id)
                                : undefined
                            }
                            sx={{
                              width: {
                                xs: "100%",
                                sm: "48%",
                                md: "32%",
                                lg: "24%",
                              },
                              minWidth: 220,
                              maxWidth: 320,
                              mb: 0,
                              pl: 2,
                              py: 2,
                              borderBottom: "none",
                              border: isBestParent
                                ? "2px solid #1976d2"
                                : "1px solid #e0e0e0",
                              borderRadius: isBestParent ? 3 : 2,
                              boxShadow: isBestParent ? 4 : 1,
                              background: isBestParent
                                ? "linear-gradient(135deg, #e3f2fd 60%, #bbdefb 100%)"
                                : "#fff",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              position: "relative",
                              cursor: canDownloadCert ? "pointer" : "default",
                              transition: "box-shadow 0.2s",
                              "&:hover": canDownloadCert
                                ? { boxShadow: 6 }
                                : {},
                            }}
                          >
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ mb: 0.5, fontSize: "1rem" }}
                            >
                              {trophy}
                              {medal}
                              {plant.number} - {plant.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={400}
                              sx={{ mb: 0.5, color: "#666" }}
                            >
                              Colecionador: {plant.collector || "Não informado"}
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={400}
                              sx={{ mb: 0.5, color: "#666" }}
                            >
                              Genealogia: {plant.genealogy || "Não informado"}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{
                                color: isBestParent ? "#1976d2" : "#333",
                                mb: 0.5,
                              }}
                            >
                              Nota final:{" "}
                              <span
                                style={{
                                  color: isBestParent ? "#1976d2" : "#222",
                                  fontWeight: 700,
                                }}
                              >
                                {plant.finalScore?.toFixed(2)}
                              </span>
                            </Typography>
                            {canDownloadCert && (
                              <Typography
                                variant="caption"
                                sx={{ mt: 0.5, color: "#1976d2", opacity: 0.8 }}
                              >
                                🏅 Clique para baixar certificado
                              </Typography>
                            )}
                          </PlantBox>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </ResultsPaper>
            );
          })}

        <Typography
          variant="h4"
          fontWeight={600}
          align="left"
          sx={{ mb: 2, color: "text.primary" }}
        >
          Melhores da exposição
        </Typography>

        {bestByParent.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              justifyContent: { xs: "center", md: "flex-start" },
              mb: 4,
            }}
          >
            {bestByParent.map(({ parentName, plant }, idx) => (
              <PlantBox
                key={plant.id}
                sx={{
                  width: { xs: "100%", sm: "48%", md: "32%", lg: "24%" },
                  minWidth: 220,
                  maxWidth: 320,
                  mb: 0,
                  pl: 2,
                  py: 2,
                  borderBottom: "none",
                  border: "2px solid #1976d2",
                  borderRadius: 3,
                  boxShadow: 4,
                  background:
                    "linear-gradient(135deg, #e3f2fd 60%, #bbdefb 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    gap: 1,
                    mb: 1,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      background: "#1976d2",
                      color: "#fff",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      boxShadow: 2,
                      letterSpacing: 0.5,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    🏆
                  </Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      color: "#1976d2",
                      fontSize: "1.15rem",
                      textShadow: "0 1px 2px #fff8",
                      ml: 1,
                    }}
                  >
                    Melhor {parentName}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ mb: 0.5, fontSize: "1.05rem" }}
                >
                  Planta {plant.number} - {plant.name}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  sx={{ mb: 0.5, color: "#666" }}
                >
                  Colecionador: {plant.collector || "Não informado"}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  sx={{ mb: 0.5, color: "#666" }}
                >
                  Genealogia: {plant.genealogy || "Não informado"}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ color: "#333", mb: 0.5 }}
                >
                  Nota final:{" "}
                  <span style={{ color: "#1976d2", fontWeight: 700 }}>
                    {plant.finalScore?.toFixed(2)}
                  </span>
                </Typography>
              </PlantBox>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};
